import React, { useEffect, useState, useCallback } from "react";
import { Search, Trash2, CheckCircle } from "lucide-react";
import * as XLSX from "xlsx-js-style";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useModal } from "../../hooks/useModal";
import { Modal } from "../ui/modal";
import LogButton from "../common/LogButton";
import { DownArrow, PlusIcon, UpArrow } from "../svg/Icons";
import RowsPerPageSelector from "../common/RowsPerPageSelector";
import Pagination from "../common/Pagination";
import { styles } from "../../styles/pages/User/UserTableStyles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import Loader from "../common/Loader";
import { ReactComponent as DownloadIcon } from "../../icons/ExcelDownloadIcon.svg";
import { Tooltip } from "../../components/common/Tooltip";

type StatusType = "Active" | "Inactive";

interface APIUser {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  profile_image: string;
  mobile_number: string;
  status: number;
  roles: string[];
  log: string;
}

interface User {
  id: number;
  full_name: string;
  email: string;
  profile_image: string;
  mobile_number: string;
  roles: string;
  status: StatusType;
  log: string;
}

const UserTable: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [users, setUsers] = useState<User[]>([]);
  const [sortColumn, setSortColumn] = useState<keyof User | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isImageModalOpen, setImageModalOpen] = useState(false); // New state for image modal
  const [selectedProfileImage, setSelectedProfileImage] = useState<
    string | null
  >(null); // Store selected image
  const { isOpen, openModal, closeModal } = useModal();
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);
  const [isDeleteSuccessModalOpen, setDeleteSuccessModalOpen] = useState(false);

  const getAuthToken = (): string | null => {
    const token = sessionStorage.getItem("access_token");
    if (!token) {
      toast.error("Authentication error! Please log in.");
      console.error("No auth token found.");
    }
    return token;
  };

  const axiosConfig = (token: string) => ({
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);

    const token = getAuthToken();
    if (!token) return;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/user/search/list`,
        { search: "" },
        axiosConfig(token)
      );

      if (response.data.status === 200) {
        const formattedUsers: User[] = Array.isArray(response.data.data)
          ? response.data.data
              .map((user: APIUser) => ({
                id: user.id,
                full_name: `${user.first_name} ${user.last_name}`,
                email: user.email,
                profile_image: user.profile_image,
                roles: user.roles.join(", "),
                mobile_number: user.mobile_number,
                status: user.status === 1 ? "Active" : "Inactive",
                log: user.log,
              }))
              .sort((a: User, b: User) => b.id - a.id)
          : [];
        setUsers(formattedUsers);
      } else {
        setUsers([]);
      }
    } catch (err) {
      setError(
        axios.isAxiosError(err) && err.response
          ? err.response.data.message || "Failed to fetch users."
          : "An unknown error occurred."
      );
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleConfirmDelete = async () => {
    if (!selectedUserId) return;

    const token = getAuthToken();
    if (!token) return;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/user/delete`,
        { user_id: selectedUserId },
        axiosConfig(token)
      );

      if (response.data.status === 200) {
        setDeleteSuccessModalOpen(true);
        setTimeout(() => setDeleteSuccessModalOpen(false), 2000);
        setUsers((prevUsers) => {
          const updatedUsers = prevUsers.filter(
            (user) => user.id !== selectedUserId
          );
          if (updatedUsers.length > 0 && displayedUsers.length === 1) {
            setCurrentPage((prevPage) => Math.max(prevPage - 1, 1));
          }
          return updatedUsers;
        });
        closeModal();
      }
    } catch (err) {
      console.error("Error deleting user:", err);
      toast.error(
        axios.isAxiosError(err) && err.response
          ? err.response.data.message || "Failed to delete user."
          : "An unexpected error occurred."
      );
    }
  };

  const toggleStatus = async (id: number) => {
    const token = getAuthToken();
    if (!token) return;

    const updatedUser = users.find((user) => user.id === id);
    if (!updatedUser) {
      toast.error("User not found.");
      return;
    }

    const newStatus = updatedUser.status === "Active" ? 0 : 1;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/user/status`,
        { user_id: id, status: newStatus },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.status === 200) {
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user.id === id
              ? { ...user, status: newStatus === 1 ? "Active" : "Inactive" }
              : user
          )
        );
        setSuccessModalOpen(true);
        setTimeout(() => setSuccessModalOpen(false), 2000);
      }
    } catch (err) {
      console.error("Error updating status:", err);
      toast.error(
        axios.isAxiosError(err) && err.response
          ? err.response.data.message || "Failed to update user status."
          : "An unexpected error occurred."
      );
    }
  };

  const filteredUsers = users.filter((user) =>
    Object.values(user)
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    if (!sortColumn) return 0;
    const valA = a[sortColumn];
    const valB = b[sortColumn];
    return typeof valA === "string" && typeof valB === "string"
      ? sortOrder === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA)
      : sortOrder === "asc"
      ? (valA as number) - (valB as number)
      : (valB as number) - (valA as number);
  });

  const displayedUsers = sortedUsers.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );
  const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);

  const handleDeleteClick = (userId: number) => {
    setSelectedUserId(userId);
    openModal();
  };

  const handleImageModalOpen = (image: string) => {
    setSelectedProfileImage(image || "https://via.placeholder.com/40");
    setImageModalOpen(true);
  };

  const handleImageModalClose = () => {
    setImageModalOpen(false);
    setSelectedProfileImage(null);
  };

  const handlePageChange = (newPage: number) => setCurrentPage(newPage);

  const handleSort = (column: keyof User) => {
    setSortColumn(column);
    setSortOrder(sortColumn === column && sortOrder === "asc" ? "desc" : "asc");
  };

  const handleDownload = () => {
    const dataToDownload = searchTerm
      ? filteredUsers
      : sortColumn
      ? sortedUsers
      : users;

    if (!dataToDownload.length) {
      toast.error("No data available to download.");
      return;
    }

    const formattedData = dataToDownload.map((user) => ({
      "Full Name": user.full_name,
      Email: user.email,
      "Mobile Number": user.mobile_number,
      Roles: user.roles,
      Status: user.status,
      Log: user.log,
    }));

    const headers = Object.keys(formattedData[0]);
    const worksheet = XLSX.utils.aoa_to_sheet([]);

    // 1. Add title in A1
    XLSX.utils.sheet_add_aoa(worksheet, [["User List"]], { origin: "A1" });

    // 2. Merge title across all columns
    worksheet["!merges"] = [
      {
        s: { r: 0, c: 0 },
        e: { r: 0, c: headers.length - 1 },
      },
    ];

    // 3. Style the title cell
    worksheet["A1"].s = {
      font: { bold: true, sz: 14 },
      alignment: { horizontal: "center", vertical: "center" },
    };

    // 4. Add headers in row 2
    XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: "A2" });

    // 5. Bold header cells
    headers.forEach((_, i) => {
      const col = String.fromCharCode(65 + i); // A, B, C...
      const cell = `${col}2`;
      if (worksheet[cell]) {
        worksheet[cell].s = {
          font: { bold: true },
        };
      }
    });

    // 6. Add data starting from row 3
    XLSX.utils.sheet_add_json(worksheet, formattedData, {
      origin: "A3",
      skipHeader: true,
    });

    // 7. Auto-size columns
    worksheet["!cols"] = headers.map((key) => ({
      wch:
        Math.max(
          key.length,
          ...formattedData.map(
            (row) =>
              (row[key as keyof typeof row] as string)?.toString().length || 0
          ),
          "User List".length
        ) + 2,
    }));

    // 8. Export to file
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Users");
    XLSX.writeFile(
      workbook,
      searchTerm ? `UserData_${searchTerm}.xlsx` : "UserData.xlsx"
    );
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [rowsPerPage]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>User List</h3>
      </div>

      <div className={styles.tableCard}>
        <div className={styles.headerControls}>
          <RowsPerPageSelector
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
          />
          <div className={styles.controlsInner}>
            <div className={styles.searchContainer}>
              <Search className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search..."
                className={styles.searchInput}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button className={styles.downloadButton} onClick={handleDownload}>
              Download
              <DownloadIcon className={styles.downloadIcon} />
            </button>
            <Link to="/user-add" className={styles.addButtonContainer}>
              <button className={styles.addButton}>
                Add New User
                <PlusIcon />
              </button>
            </Link>
          </div>
        </div>
        {error && <p className={styles.errorText}>{error}</p>}

        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr className={styles.theadRow}>
                <th
                  className={styles.thName}
                  onClick={() => handleSort("full_name")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Name</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                <th className={styles.thProfileImage}>
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Profile</p>
                  </div>
                </th>
                <th
                  className={styles.thEmail}
                  onClick={() => handleSort("email")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Email</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                <th
                  className={styles.thMobileNumber}
                  onClick={() => handleSort("mobile_number")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Mobile Number</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                <th
                  className={styles.thRole}
                  onClick={() => handleSort("roles")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Role</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                <th className={styles.thStatus}>
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Status</p>
                  </div>
                </th>
                <th className={styles.thAction}>
                  <p className={styles.thText}>Action</p>
                </th>
                <th className={styles.thLog}>
                  <p className={styles.thText}>Log</p>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr className={styles.loadingRow}>
                  <td colSpan={8} className={styles.loadingText}>
                    <Loader />
                  </td>
                </tr>
              ) : displayedUsers.length > 0 ? (
                displayedUsers.map((user) => (
                  <tr key={user.id} className={styles.tbodyRow}>
                    <td className={styles.tdName}>{user.full_name}</td>
                    <td className={styles.tdProfileImage}>
                      <img
                        src={
                          user.profile_image || "https://via.placeholder.com/40"
                        }
                        alt={user.full_name}
                        className={`${styles.profileImage} cursor-pointer`}
                        onClick={() => handleImageModalOpen(user.profile_image)}
                      />
                    </td>
                    <td className={styles.tdEmail}>{user.email}</td>
                    <td className={styles.tdMobileNumber}>
                      {user.mobile_number}
                    </td>
                    <td className={styles.tdRole}>{user.roles}</td>
                    <td className={styles.tdStatus}>
                      <div className={styles.statusContainer}>
                        <button
                          onClick={() => toggleStatus(user.id)}
                          className={`${styles.statusToggle} ${
                            user.status === "Active"
                              ? styles.statusToggleActive
                              : styles.statusToggleInactive
                          }`}
                        >
                          <div
                            className={`${styles.statusToggleCircle} ${
                              user.status === "Active"
                                ? styles.statusToggleCircleActive
                                : styles.statusToggleCircleInactive
                            }`}
                          ></div>
                        </button>
                        <span
                          className={`${styles.statusBadge} ${
                            user.status === "Active"
                              ? styles.statusActive
                              : styles.statusInactive
                          }`}
                        >
                          {user.status}
                        </span>
                      </div>
                    </td>
                    <td className={styles.tdAction}>
                      <div className={styles.actionContainer}>
                        <Tooltip content="Edit" bgColor="bg-[#2f6aa7]">
                        <Link
                          to={`/user-edit/${user.id}`}
                          className="inline-flex"
                        >
                          <button className={styles.editButton}>
                            <FontAwesomeIcon
                              icon={faEdit}
                              className="text-white text-sm"
                            />
                          </button>
                        </Link>
                        </Tooltip>
                        <Tooltip content="Delete" bgColor="bg-red-600">
                        <button
                          className={styles.deleteButton}
                          onClick={() => handleDeleteClick(user.id)}
                        >
                          <FontAwesomeIcon
                            icon={faTrash}
                            className="text-white text-sm"
                          />
                        </button>
                        </Tooltip>
                      </div>
                    </td>
                    <td className={styles.tdLog}>
                      <div className={styles.actionContainer}>
                        <LogButton log={user.log} />
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr className={styles.loadingRow}>
                  <td colSpan={8} className={styles.noDataText}>
                    No records match your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          rowsPerPage={rowsPerPage}
          filteredLength={filteredUsers.length}
          handlePageChange={handlePageChange}
        />
      </div>

      <Modal isOpen={isOpen} onClose={closeModal} className={styles.modal}>
        <button
          onClick={closeModal}
          className={styles.modalCloseButton}
        ></button>
        <div className="flex justify-center">
          <div className={styles.modalIconContainer}>
            <Trash2 className={styles.modalIcon} />
          </div>
        </div>
        <h4 className={styles.modalTitle}>Confirm Delete</h4>
        <p className={styles.modalText}>
          Are you sure you want to delete this user?
        </p>
        <div className={styles.modalButtonContainer}>
          <button onClick={closeModal} className={styles.modalCancelButton}>
            Cancel
          </button>
          <button
            onClick={handleConfirmDelete}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      </Modal>

      {/* Success Modal for Status Update */}
      <Modal
        isOpen={isSuccessModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        className="w-[80%] max-w-[400px] bg-white dark:bg-gray-900 px-6 py-6 rounded-lg shadow-xl mx-auto text-center relative"
      >
        <div className="flex justify-center">
          <div className="w-14 h-14 flex items-center justify-center bg-green-100 text-green-600 rounded-full">
            <CheckCircle className="w-6 h-6" />
          </div>
        </div>
        <h4 className="text-theme-xl font-bold text-gray-800 dark:text-white mt-4">
          User Status Updated Successfully!
        </h4>
        <div className="mt-6 flex justify-center">
          <button
            onClick={() => setSuccessModalOpen(false)}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            OK
          </button>
        </div>
      </Modal>

      {/* Success Modal for Delete */}
      <Modal
        isOpen={isDeleteSuccessModalOpen}
        onClose={() => setDeleteSuccessModalOpen(false)}
        className="w-[80%] max-w-[400px] bg-white dark:bg-gray-900 px-6 py-6 rounded-lg shadow-xl mx-auto text-center relative"
      >
        <div className="flex justify-center">
          <div className="w-14 h-14 flex items-center justify-center bg-green-100 text-green-600 rounded-full">
            <CheckCircle className="w-6 h-6" />
          </div>
        </div>
        <h4 className="text-theme-xl font-bold text-gray-800 dark:text-white mt-4">
          User Deleted Successfully!
        </h4>
        <div className="mt-6 flex justify-center">
          <button
            onClick={() => setDeleteSuccessModalOpen(false)}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            OK
          </button>
        </div>
      </Modal>

      {/* Profile Image Modal */}
      {selectedProfileImage && (
        <Modal
          isOpen={isImageModalOpen}
          onClose={handleImageModalClose}
          className="w-[70%] max-w-lg bg-white dark:bg-gray-900 p-6 rounded-lg shadow-xl mx-auto text-center"
        >
          <div className="flex justify-center">
            <img
              src={selectedProfileImage}
              alt="User Profile Enlarged"
              className="w-full h-auto max-h-[60vh] rounded object-contain"
            />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default UserTable;
